<script setup lang="ts">
import { computed } from "vue";
import { bemBuilder } from "@chatfood/core-utils";
import {
  AtomText,
  AtomIcon,
  type IAtomIconProps,
} from "@chatfood/design-system";
import type { SocialMediaLinksProps } from "./props";
import { useBusinessStore } from "~/stores/business";
import { useLocale } from "~/composables/use-locale";

const css = bemBuilder("social-media-links");

const props = withDefaults(defineProps<SocialMediaLinksProps>(), {
  hideTitle: false,
});
const { t } = useLocale();
const business = useBusinessStore();

const platforms = [
  "facebook",
  "instagram",
  "x_twitter_",
  "linkedin",
  "youtube",
  "tiktok",
];

const supportedSocialMedias = computed(() => {
  const socialLinks = business.siteLinks?.social || [];
  return socialLinks.filter((social) => platforms.includes(social.slug));
});
</script>

<template>
  <div v-if="supportedSocialMedias.length" :class="css('')">
    <AtomText
      v-if="!props.hideTitle"
      :class="css('title')"
      type="h3"
      color="color-text-body"
      data-test="title"
    >
      {{ t("components.social_media_links.title") }}
    </AtomText>
    <div :class="css('list')">
      <a
        v-for="(socialMedia, index) in supportedSocialMedias"
        :key="index"
        target="_blank"
        :class="css('link')"
        :href="socialMedia.url"
        :title="socialMedia.name"
        rel="noopener noreferrer"
      >
        <AtomIcon
          v-if="socialMedia.slug === 'x_twitter_'"
          :size="32"
          color="color-primary"
          name="twitter"
        />
        <AtomIcon
          v-else
          :size="32"
          color="color-primary"
          :name="socialMedia.slug as IAtomIconProps['name']"
        />
      </a>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.social-media-links {
  display: flex;
  gap: var(--spacing-sm);
  flex-direction: column;

  &__list {
    display: flex;
    width: 100%;
    align-items: center;
    gap: var(--spacing-md);

    @media (width > $breakpoint-sm) {
      justify-content: flex-start;
    }
  }

  &__link {
    display: flex;
  }
}
</style>
